import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { Check, ChevronRight, ChevronLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { ENGAGEMENT_TYPES as TYPES } from "@/lib/constants";

const steps = ["Type", "Client", "Team", "Period", "Confirm"];

export default function EngagementWizard() {
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [users, setUsers] = useState([]);
    useEffect(() => { api.get("/users?role=staff").then((r) => setUsers(r.data)).catch(() => {}); }, []);
    const [form, setForm] = useState({
        engagement_type: "review_only",
        client_name: "",
        managing_agent: "",
        preparer_id: "",
        auditor_id: "",
        manager_id: "",
        partner_id: "",
        current_period_start: "",
        current_period_end: "",
        has_prior_period: true,
        prior_period_start: "",
        prior_period_end: "",
        has_budget: true,
        notes: "",
        service_charge_areas: [{ id: "1", name: "General", has_budget: true }],
    });
    const [busy, setBusy] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const canNext = () => {
        if (step === 0) return !!form.engagement_type;
        if (step === 1) return !!form.client_name;
        if (step === 2) return !!form.preparer_id && !!form.manager_id && !!form.partner_id && (form.engagement_type !== "prep_audit" || !!form.auditor_id);
        if (step === 3) return !!form.current_period_start && !!form.current_period_end;
        return true;
    };

    const submit = async () => {
        setBusy(true);
        try {
            const payload = {
                ...form,
                prior_period_start: form.has_prior_period ? form.prior_period_start : null,
                prior_period_end: form.has_prior_period ? form.prior_period_end : null,
                preparer_id: form.preparer_id || null,
                auditor_id: form.auditor_id || null,
                manager_id: form.manager_id || null,
                partner_id: form.partner_id || null,
            };
            const { data } = await api.post("/engagements", payload);
            toast.success("Engagement created — working papers auto-generated");
            nav(`/engagements/${data.id}`);
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail));
        } finally { setBusy(false); }
    };

    return (
        <div className="p-8 max-w-4xl" data-testid="wizard-page">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display mb-1">New engagement</div>
                <h1 className="font-display font-bold text-3xl tracking-tight">Setup a new working paper file</h1>
            </div>

            {/* Steps */}
            <div className="flex items-center gap-0 mb-8 bg-white border border-border rounded-sm p-1" data-testid="wizard-steps">
                {steps.map((s, i) => (
                    <div key={s} className={`flex-1 flex items-center gap-2 px-3 py-2 text-xs rounded-sm ${
                        i === step ? "bg-brand text-white font-semibold" : i < step ? "text-brand" : "text-muted-foreground"
                    }`}>
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${
                            i === step ? "bg-white text-brand" : i < step ? "bg-brand text-white" : "bg-muted"
                        }`}>{i < step ? <Check className="w-3 h-3" /> : i + 1}</div>
                        <span className="font-display uppercase tracking-wider text-[11px]">{s}</span>
                    </div>
                ))}
            </div>

            <div className="bg-white border border-border rounded-sm p-6 min-h-[380px]">
                {step === 0 && (
                    <div className="space-y-2" data-testid="wizard-step-type">
                        <div className="text-[11px] uppercase tracking-widest font-display text-muted-foreground mb-2">What kind of engagement is this?</div>
                        {TYPES.map((t) => (
                            <label key={t.v} className={`flex items-start gap-3 border rounded-sm px-4 py-3 cursor-pointer transition-colors ${
                                form.engagement_type === t.v ? "border-brand bg-brand/[0.03] ring-1 ring-brand" : "border-border hover:bg-slate-50"
                            }`}>
                                <input type="radio" data-testid={`wizard-type-${t.v}`} name="etype" checked={form.engagement_type === t.v} onChange={() => update("engagement_type", t.v)} className="mt-1 accent-brand" />
                                <div>
                                    <div className="font-display font-semibold text-sm">{t.label}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{t.desc}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                )}

                {step === 1 && (
                    <div className="grid grid-cols-2 gap-4" data-testid="wizard-step-client">
                        <Field label="Client name *"><input data-testid="wizard-client-name" className="w-full border border-input rounded-sm px-3 py-2 text-sm" value={form.client_name} onChange={(e) => update("client_name", e.target.value)} /></Field>
                        <Field label="Managing agent">
                            <ManagingAgentPicker value={form.managing_agent} onChange={(v) => update("managing_agent", v)} />
                        </Field>
                        <div className="col-span-2 text-[11px] text-muted-foreground -mt-1">Materiality is not entered here — it is calculated on working paper A02 (audit) or documented in planning.</div>
                        <div className="col-span-2">
                            <Field label="Notes"><textarea data-testid="wizard-notes" rows={3} className="w-full border border-input rounded-sm px-3 py-2 text-sm" value={form.notes} onChange={(e) => update("notes", e.target.value)} /></Field>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-4" data-testid="wizard-step-team">
                        <div className="text-[11px] uppercase tracking-widest font-display text-muted-foreground">Engagement team</div>
                        <p className="text-xs text-muted-foreground">The preparer ticks off steps and marks working papers complete; the manager and partner approve them. {form.engagement_type === "prep_audit" && "The auditor reviews the prep papers and owns the audit papers. "}The same person may hold more than one role.</p>
                        <div className={`grid gap-4 ${form.engagement_type === "prep_audit" ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-3"}`}>
                            {(form.engagement_type === "prep_audit"
                                ? [["preparer_id", "Preparer *"], ["auditor_id", "Auditor *"], ["manager_id", "Manager *"], ["partner_id", "Partner *"]]
                                : [["preparer_id", "Preparer *"], ["manager_id", "Manager *"], ["partner_id", "Partner *"]]).map(([k, label]) => (
                                <Field key={k} label={label}>
                                    <select data-testid={`wizard-${k}`} value={form[k]} onChange={(e) => update(k, e.target.value)} className="w-full border border-input rounded-sm px-3 py-2 text-sm bg-white">
                                        <option value="">Select…</option>
                                        {users.filter((u) => k !== "partner_id" || u.can_partner).filter((u) => k !== "manager_id" || u.can_manager).map((u) => (
                                            <option key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role}` : ""}</option>
                                        ))}
                                    </select>
                                </Field>
                            ))}
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-5" data-testid="wizard-step-period">
                        <div>
                            <div className="font-display uppercase tracking-widest text-[11px] text-muted-foreground mb-2">Current period *</div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Start"><input data-testid="wizard-current-start" type="date" className="w-full border border-input rounded-sm px-3 py-2 text-sm" value={form.current_period_start} onChange={(e) => update("current_period_start", e.target.value)} /></Field>
                                <Field label="End"><input data-testid="wizard-current-end" type="date" className="w-full border border-input rounded-sm px-3 py-2 text-sm" value={form.current_period_end} onChange={(e) => update("current_period_end", e.target.value)} /></Field>
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input data-testid="wizard-has-prior" type="checkbox" checked={form.has_prior_period} onChange={(e) => update("has_prior_period", e.target.checked)} className="accent-brand" />
                            Include prior period comparative
                        </label>
                        {form.has_prior_period && (
                            <div>
                                <div className="font-display uppercase tracking-widest text-[11px] text-muted-foreground mb-2">Prior period (may differ in length)</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Start"><input data-testid="wizard-prior-start" type="date" className="w-full border border-input rounded-sm px-3 py-2 text-sm" value={form.prior_period_start} onChange={(e) => update("prior_period_start", e.target.value)} /></Field>
                                    <Field label="End"><input data-testid="wizard-prior-end" type="date" className="w-full border border-input rounded-sm px-3 py-2 text-sm" value={form.prior_period_end} onChange={(e) => update("prior_period_end", e.target.value)} /></Field>
                                </div>
                            </div>
                        )}
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input data-testid="wizard-has-budget" type="checkbox" checked={form.has_budget} onChange={(e) => update("has_budget", e.target.checked)} className="accent-brand" />
                            Budget column will be provided (per service charge area)
                        </label>
                    </div>
                )}

                {step === 4 && (
                    <div className="space-y-4" data-testid="wizard-step-confirm">
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-display">Confirm and generate working paper file</div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm border border-border rounded-sm p-4 bg-slate-50">
                            <Row k="Engagement type" v={TYPES.find((t) => t.v === form.engagement_type)?.label} />
                            <Row k="Client" v={form.client_name} />
                            <Row k="Managing agent" v={form.managing_agent || "—"} />
                            <Row k="Team" v={(form.engagement_type === "prep_audit" ? ["preparer_id", "auditor_id", "manager_id", "partner_id"] : ["preparer_id", "manager_id", "partner_id"]).map((k) => users.find((u) => u.id === form[k])?.name || "—").join(" / ")} />
                            <Row k="Current period" v={`${form.current_period_start} → ${form.current_period_end}`} />
                            <Row k="Prior period" v={form.has_prior_period ? `${form.prior_period_start} → ${form.prior_period_end}` : "None"} />
                            <Row k="Budget" v={form.has_budget ? "Yes" : "No"} />
                        </div>
                        <p className="text-xs text-muted-foreground">A working paper file will be auto-generated from the master template set, filtered to the engagement type you selected. Service-charge schedules are picked up automatically when you import and map the trial balance — you don't need to name them here.</p>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between mt-4">
                <button
                    disabled={step === 0}
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="wizard-back-btn"
                    className="flex items-center gap-1 px-4 py-2 text-sm rounded-sm border border-border hover:bg-slate-50 disabled:opacity-40">
                    <ChevronLeft className="w-4 h-4" /> Back
                </button>
                {step < steps.length - 1 ? (
                    <button
                        disabled={!canNext()}
                        onClick={() => setStep((s) => s + 1)}
                        data-testid="wizard-next-btn"
                        className="flex items-center gap-1 px-4 py-2 text-sm rounded-sm bg-brand hover:bg-brand-hover text-white font-semibold disabled:opacity-40">
                        Next <ChevronRight className="w-4 h-4" />
                    </button>
                ) : (
                    <button
                        disabled={busy}
                        onClick={submit}
                        data-testid="wizard-submit-btn"
                        className="px-5 py-2 text-sm rounded-sm bg-brand hover:bg-brand-hover text-white font-semibold disabled:opacity-40">
                        {busy ? "Creating…" : "Create engagement"}
                    </button>
                )}
            </div>
        </div>
    );
}

function ManagingAgentPicker({ value, onChange }) {
    const [agents, setAgents] = useState([]);
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState("");
    const load = () => api.get("/managing-agents").then((r) => setAgents(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);
    const add = async () => {
        if (!name.trim()) return;
        try {
            const { data } = await api.post("/managing-agents", { name: name.trim() });
            await load(); onChange(data.name); setName(""); setAdding(false);
            toast.success("Managing agent added");
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    return (
        <div>
            <div className="flex gap-2">
                <select data-testid="wizard-managing-agent" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 border border-input rounded-sm px-3 py-2 text-sm bg-white">
                    <option value="">Select managing agent…</option>
                    {agents.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
                <button type="button" data-testid="wizard-add-agent-btn" onClick={() => setAdding((v) => !v)} className="flex items-center gap-1 text-xs border border-border rounded-sm px-2.5 hover:bg-slate-50 whitespace-nowrap"><Plus className="w-3.5 h-3.5" /> New</button>
            </div>
            {adding && (
                <div className="flex gap-2 mt-2" data-testid="wizard-add-agent-form">
                    <input data-testid="wizard-new-agent-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Managing agent name" className="flex-1 border border-input rounded-sm px-3 py-1.5 text-sm" />
                    <button type="button" data-testid="wizard-new-agent-save" onClick={add} className="bg-brand hover:bg-brand-hover text-white text-xs px-3 rounded-sm">Add</button>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-display block mb-1.5">{label}</label>
            {children}
        </div>
    );
}

function Row({ k, v }) {
    return (
        <>
            <div className="text-muted-foreground text-xs uppercase tracking-wider font-display">{k}</div>
            <div className="font-medium">{v}</div>
        </>
    );
}
